import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  getRiskFieldLabelProps,
  getRiskFieldValueProps,
  getRiskSectionTitleSx,
  riskButtonSx,
} from './riskComplianceStyles';

export default function DnbMatchResultsModal({
  open,
  onClose,
  candidates = [],
  labels,
  onLink,
  onInvestigate,
  isLinking,
}) {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const total = candidates.length;
  const candidate = candidates[index];

  const handleClose = () => {
    setIndex(0);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography variant="h6" sx={getRiskSectionTitleSx(theme)}>
          {labels.title}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
          {total > 0 ? labels.pagination.replace('{current}', index + 1).replace('{total}', total) : labels.noResults}
        </Typography>
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {candidate ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1.5,
              p: 2.5,
              bgcolor: theme.palette.action.hover,
              maxHeight: 420,
              overflowY: 'auto',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, gap: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: theme.typography.fontWeightBold,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {candidate.primaryName}
              </Typography>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography
                  variant="h6"
                  color="success.main"
                  fontWeight={theme.typography.fontWeightBold}
                  lineHeight={1.2}
                >
                  {Math.round(candidate.matchConfidencePercent)}%
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {labels.matchLabel}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={0} sx={{ mb: 2.5 }}>
              <MatchField theme={theme} label={labels.tradeStyle} value={candidate.tradeStyle || '—'} />
              <MatchField theme={theme} label={labels.duns} value={candidate.duns} />
              <MatchField theme={theme} label={labels.address} value={candidate.address} />
              <MatchField theme={theme} label={labels.registrationNumber} value={candidate.registrationNumber || '—'} />
              <MatchField theme={theme} label={labels.status} value={candidate.operatingStatus || '—'} />
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={() =>
                  onLink({
                    dunsNumber: candidate.duns.replace(/\D/g, ''),
                    source: 'match',
                    companyName: candidate.primaryName,
                  })
                }
                disabled={isLinking}
                sx={riskButtonSx}
              >
                {labels.linkDuns}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {labels.noResults}
          </Typography>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5 }}>
          <Button
            variant="text"
            onClick={onInvestigate}
            disabled={!onInvestigate}
            sx={{
              color: theme.palette.text.secondary,
              textTransform: 'none',
              textDecoration: 'underline',
              p: 0,
              minWidth: 0,
            }}
          >
            {labels.investigate}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              disabled={index <= 0}
              onClick={() => setIndex((value) => value - 1)}
              sx={{ ...riskButtonSx, minWidth: 0 }}
            >
              {labels.prev}
            </Button>
            <Button
              variant="text"
              disabled={index >= total - 1}
              onClick={() => setIndex((value) => value + 1)}
              sx={{ ...riskButtonSx, minWidth: 0, fontWeight: theme.typography.fontWeightBold }}
            >
              {labels.next}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function MatchField({ theme, label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography {...getRiskFieldLabelProps(theme)}>{label}</Typography>
      <Typography {...getRiskFieldValueProps(theme)}>{value}</Typography>
    </Box>
  );
}
